import type { Edge, Node } from 'reactflow';

import type { ProblemFlowSearchIDResponse } from 'api/process';
import type { CreateEditEdgePayload } from 'api/client';

import type { EdgeForPost, Payload, Select, StatusFlowByID } from './types';

const select: Select = (data) =>
  data.data.data.map((dt) => ({ ...dt, label: dt.name }));

export { select };

const transformProblemFlowDataForEdit = (
  problemFlowDataForEdit: ProblemFlowSearchIDResponse
): StatusFlowByID[] => {
  const groupedByStatus = problemFlowDataForEdit.edges.reduce<{
    [key: number]: StatusFlowByID;
  }>((acc, edge) => {
    const { initialStatus, finalStatus, isInitialStatus } = edge;
    const statusId = initialStatus.id;

    if (!acc[statusId]) {
      acc[statusId] = {
        statusId,
        name: initialStatus.description,
        statusNumberOfChanges: 0,
        base: false,
        statusSelected: []
      };
    }
    if (isInitialStatus) acc[statusId].isInitialStatus = true;

    if (finalStatus) {
      acc[statusId].statusSelected.push({
        label: finalStatus.description,
        id: finalStatus.id,
        checked: true,
        base: false
      });
    }

    return acc;
  }, {});

  Object.keys(groupedByStatus).forEach((key) => {
    const statusId = parseInt(key, 10);
    if (!Number.isNaN(statusId)) {
      groupedByStatus[statusId].statusNumberOfChanges =
        groupedByStatus[statusId].statusSelected.length;
    }
  });

  const resultArray: Payload[] = Object.values(groupedByStatus);

  return resultArray;
};

export { transformProblemFlowDataForEdit };

/**
 * Transforma los datos del formato Payload al formato requerido por la API
 * NOTA: Esta función parece no estar en uso actualmente. Se mantiene por compatibilidad.
 *
 * @param dataForTheTable - Array de datos en formato Payload
 * @returns Array de objetos CreateEditEdgePayload listos para enviar a la API
 */
const transformDataForPutOrPost = (
  dataForTheTable: Payload[]
): CreateEditEdgePayload[] => {
  const transformedData: CreateEditEdgePayload[] = [];

  let setIsInitialStatusFlag = true;
  dataForTheTable.forEach((statusItem) => {
    statusItem.statusSelected.forEach((selectedItem) => {
      transformedData.push({
        initialStatusId: statusItem.statusId,
        finalStatusId: selectedItem.id,
        isInitialStatus: statusItem?.isInitialStatus && setIsInitialStatusFlag
      });
      if (statusItem?.isInitialStatus) setIsInitialStatusFlag = false;
    });
  });

  return transformedData;
};

export { transformDataForPutOrPost };

/**
 * Transforma los datos del formato DataForTheTable (ProblemManager)
 * al formato requerido por la API para creación/edición de flujos
 *
 * @param dataForTheTable - Array de datos en formato DataForTheTable
 * @returns Array de objetos CreateEditEdgePayload listos para enviar a la API
 */
const transformProblemFlowDataForAPI = (
  dataForTheTable: StatusFlowByID[]
): CreateEditEdgePayload[] => {
  const transformedData: CreateEditEdgePayload[] = [];
  let isFirstEdge = true;

  dataForTheTable?.forEach((statusItem) => {
    if (!statusItem?.statusId || !statusItem?.statusSelected?.length) return;

    statusItem.statusSelected.forEach((edge: EdgeForPost) => {
      if (!edge?.id) return;

      transformedData.push({
        initialStatusId: statusItem.statusId,
        finalStatusId: edge.id,
        isInitialStatus: isFirstEdge
      });

      if (isFirstEdge) isFirstEdge = false;
    });
  });

  return transformedData;
};

export { transformProblemFlowDataForAPI };

interface CustomNodeData {
  color?: string;
}

export type CustomNode = Node<CustomNodeData>;

// Constantes para estilos de nodos y aristas
const DEFAULT_NODE_BACKGROUND = '#15243e';
const DEFAULT_EDGE_STROKE = 'black';
const DEFAULT_EDGE_STROKE_WIDTH = 1.5;
const SELECTED_EDGE_STROKE_WIDTH = 3.5;

/**
 * Actualiza el estilo de los nodos cuando se selecciona uno
 *
 * @param nodes - Array de nodos actuales
 * @param node - Nodo seleccionado
 * @param selectedColor - Color a aplicar al nodo seleccionado
 * @returns Array de nodos con estilos actualizados
 */
export const updateNodeStyle = (
  nodes: CustomNode[],
  node: CustomNode,
  selectedColor: string
): CustomNode[] =>
  nodes.map((n) => {
    if (n.id === node.id && n.style?.background !== selectedColor) {
      return { ...n, style: { ...n.style, background: selectedColor } };
    }
    if (n.id !== node.id && n.style?.background !== DEFAULT_NODE_BACKGROUND) {
      return {
        ...n,
        style: { ...n.style, background: DEFAULT_NODE_BACKGROUND }
      };
    }
    return n;
  });

/**
 * Actualiza el estilo de las aristas cuando se selecciona un nodo
 *
 * @param edges - Array de aristas actuales
 * @param node - Nodo seleccionado
 * @param selectedColor - Color a aplicar a las aristas conectadas
 * @returns Array de aristas con estilos actualizados
 */
export const updateEdgeStyle = (
  edges: Edge[],
  node: CustomNode,
  selectedColor: string
): Edge[] =>
  edges.map((e) => {
    const isConnectedToNode = e.source === node.id || e.target === node.id;

    if (isConnectedToNode && e.style?.stroke !== selectedColor) {
      return {
        ...e,
        style: {
          ...e.style,
          stroke: selectedColor,
          strokeWidth: SELECTED_EDGE_STROKE_WIDTH
        }
      };
    }

    if (!isConnectedToNode && e.style?.stroke !== DEFAULT_EDGE_STROKE) {
      return {
        ...e,
        style: {
          ...e.style,
          stroke: DEFAULT_EDGE_STROKE,
          strokeWidth: DEFAULT_EDGE_STROKE_WIDTH
        }
      };
    }

    return e;
  });
